import { ProjectsService } from '@beeboard/common';
import { defineStore } from 'pinia';
import { useProjectStore } from '@/store/projectStore';
import { usePhaseProjectRulesStore } from './phaseProjectRulesStore';
import type {
  Phase,
  PhaseCreate,
  PhaseUpdate,
  PhaseSplitRequest,
  PhaseShiftElementsRequest,
  CompressInPhaseRequest,
} from '@beeboard/common';

export const PHASE_MIN_DURATION_DAYS = 1;

export const usePhaseProjectStore = defineStore('phaseProjectStore', {
  getters: {
    phases() {
      return function (projectId: string): Phase[] {
        const projectStore = useProjectStore();
        const project = projectStore.project(projectId);

        return project.phases;
      };
    },

    phase() {
      return (projectId: string, phaseId: string): Phase => {
        const phases = this.phases(projectId);
        const phase = phases.find((phase) => phase.id === phaseId);
        if (!phase) {
          throw new Error(
            'Missing phase ' + phaseId + 'in project ' + projectId,
          );
        }

        return phase;
      };
    },

    phaseForMilestone() {
      return (projectId: string, milestoneId: string) => {
        const phases = this.phases(projectId);
        const phase = phases.find((phase) => {
          const milestones = [
            phase.final_milestone,
            ...phase.intermediate_milestones,
          ].map((m) => m.id);

          return milestones.includes(milestoneId);
        });

        return phase;
      };
    },

    hasActivities() {
      return (projectId: string, phaseId: string) => {
        const phase = this.phase(projectId, phaseId);
        const milestones = [
          phase.final_milestone,
          ...phase.intermediate_milestones,
        ];

        return milestones.find((m) => m.activities.length > 0);
      };
    },
  },

  actions: {
    write(projectId: string, phase: Phase): Phase {
      const phases = this.phases(projectId);
      const oldPhase = phases.find((pha) => pha.id === phase.id);
      if (oldPhase) {
        Object.assign(oldPhase, phase);
      } else {
        phases.push(phase);
      }

      return this.phase(projectId, phase.id);
    },

    async add(projectId: string, payload: PhaseCreate, before?: string) {
      const phase = await ProjectsService.addProjectPhase(
        projectId,
        payload,
        before,
      );
      if (before) {
        const phases = this.phases(projectId);
        const beforeIndex = phases.findIndex((pha) => pha.id === before);
        phases.splice(beforeIndex, 0, phase);
        await useProjectStore().loadProject(projectId);

        return this.phase(projectId, phase.id);
      } else {
        return this.write(projectId, phase);
      }
    },

    async update(projectId: string, phaseId: string, payload: PhaseUpdate) {
      await ProjectsService.updateProjectPhase(projectId, phaseId, payload);
      await useProjectStore().loadProject(projectId);
    },

    async applySpaceTool(
      projectId: string,
      phaseId: string,
      payload: PhaseShiftElementsRequest,
    ) {
      await ProjectsService.shiftPhaseElements(projectId, phaseId, payload);
      await useProjectStore().loadProject(projectId);
    },

    async applyCompressTool(
      projectId: string,
      phaseId: string,
      payload: CompressInPhaseRequest,
    ) {
      await ProjectsService.compressInPhase(projectId, phaseId, payload);
      await useProjectStore().loadProject(projectId);
    },

    async split(
      projectId: string,
      phaseId: string,
      payload: PhaseSplitRequest,
    ) {
      const errors = usePhaseProjectRulesStore().validateSplitPhaseRequest(
        projectId,
        phaseId,
        payload,
      );
      if (errors) {
        throw errors;
      }
      const splittedPhases = await ProjectsService.splitProjectPhase(
        projectId,
        phaseId,
        payload,
      );
      const projectStore = useProjectStore();
      await projectStore.loadProject(projectId);

      return splittedPhases[0];
    },

    async distribute(projectId: string, phaseId: string) {
      await ProjectsService.distributeInPhase(projectId, phaseId);
      const projectStore = useProjectStore();
      await projectStore.loadProject(projectId);
    },

    async remove(projectId: string, phaseId: string) {
      const errors = usePhaseProjectRulesStore().validateRemovePhaseRequest(
        projectId,
        phaseId,
      );
      if (errors) {
        throw errors;
      }
      await ProjectsService.removeProjectPhase(projectId, phaseId);
      const projectStore = useProjectStore();
      await projectStore.loadProject(projectId);
    },
  },
});
